import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, MoveHorizontal, Check } from 'lucide-react';
import { useComparison } from './ComparisonContext';
import { useTheme } from '../styles/ThemeContext';
import { useSelections } from './SelectionsContext';
export function ComparisonView() {
  const {
    comparisonItems,
    isComparisonMode,
    toggleComparisonMode,
    clearComparison
  } = useComparison();
  const {
    addSelection
  } = useSelections();
  const {
    isDark
  } = useTheme();
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Mock scenes for the items - in a real app these would be the 3D renders
  // For now we'll use the thumbnails or placeholders if thumbnails are too small
  const leftItem = comparisonItems[0];
  const rightItem = comparisonItems[1];
  // Use high-res placeholders if thumbnails are small icons
  const getSceneImage = (item: any) => {
    if (!item) return '';
    // If it's a fridge, return a kitchen scene with that fridge (mocked)
    // For this prototype, we'll just use the item's thumbnail blown up,
    // or a generic scene if available.
    // Let's use the background images from InteriorViewer but maybe tinted to show difference?
    // Actually, let's just use the thumbnails for the "Option Comparison" mode
    // since we don't have full scene renders for every option in this mock data.
    return item.thumbnail || '';
  };
  const handleDrag = (e: React.MouseEvent | React.TouchEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const position = (clientX - rect.left) / rect.width * 100;
    setSliderPosition(Math.min(100, Math.max(0, position)));
  };
  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    // Prevent text selection
    if ('preventDefault' in e) e.preventDefault();
    setIsDragging(true);
  };
  const handleMouseUp = () => setIsDragging(false);
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchend', handleMouseUp);
      window.addEventListener('mousemove', handleDrag as any);
      window.addEventListener('touchmove', handleDrag as any);
    }
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
      window.removeEventListener('mousemove', handleDrag as any);
      window.removeEventListener('touchmove', handleDrag as any);
    };
  }, [isDragging]);
  const handleSelectOption = (item: any) => {
    addSelection({
      category: item.parentCategory || 'Kitchen',
      itemName: item.category || 'Option',
      optionName: item.name,
      optionId: item.id,
      price: item.price,
      thumbnail: item.thumbnail,
      isPackageItem: false
    });
    clearComparison();
  };
  if (!isComparisonMode || comparisonItems.length < 2) return null;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex flex-col bg-black/90 backdrop-blur-sm" data-id="element-578">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 z-10" data-id="element-579">
        <h2 className="text-white text-xl font-bold tracking-wider" data-id="element-580">
          Compare Options
        </h2>
        <button onClick={toggleComparisonMode} className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" data-id="element-581">
          <X className="w-6 h-6" data-id="element-582" />
        </button>
      </div>

      {/* Comparison Area */}
      <div className="flex-1 relative flex items-center justify-center p-8 overflow-hidden select-none" data-id="element-583">
        <div ref={containerRef} className="relative w-full max-w-6xl aspect-video rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-gray-900 cursor-ew-resize" onMouseDown={handleMouseDown} onTouchStart={handleMouseDown} data-id="element-584">
          {/* Right Image (Background) */}
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800 select-none pointer-events-none" data-id="element-585">
            {rightItem?.thumbnail ? <img src={rightItem.thumbnail} alt={rightItem.name} className="w-full h-full object-contain select-none pointer-events-none" draggable={false} data-id="element-586" /> : <div className="text-white/50" data-id="element-587">No Image</div>}
            <div className="absolute top-4 right-4 bg-black/60 backdrop-blur px-3 py-1.5 rounded-lg text-white text-sm font-bold border border-white/10" data-id="element-588">
              {rightItem?.name}
            </div>
          </div>

          {/* Left Image (Foreground - Clipped) */}
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800 border-r border-white/50 select-none pointer-events-none" style={{
          clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
        }} data-id="element-589">
            {leftItem?.thumbnail ? <img src={leftItem.thumbnail} alt={leftItem.name} className="w-full h-full object-contain select-none pointer-events-none" draggable={false} data-id="element-590" /> : <div className="text-white/50" data-id="element-591">No Image</div>}
            <div className="absolute top-4 left-4 bg-black/60 backdrop-blur px-3 py-1.5 rounded-lg text-white text-sm font-bold border border-white/10" data-id="element-592">
              {leftItem?.name}
            </div>
          </div>

          {/* Slider Handle */}
          <div className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize shadow-[0_0_10px_rgba(0,0,0,0.5)] z-20 pointer-events-none" style={{
          left: `${sliderPosition}%`
        }} data-id="element-593">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-900 pointer-events-auto" data-id="element-594">
              <MoveHorizontal className="w-5 h-5" data-id="element-595" />
            </div>
          </div>
        </div>
      </div>

      {/* Footer / Details */}
      <div className="h-48 bg-gray-900 border-t border-white/10 p-6 grid grid-cols-2 gap-8" data-id="element-596">
        {/* Left Item Details */}
        <div className="flex flex-col items-end text-right pr-8 border-r border-white/10" data-id="element-597">
          <h3 className="text-2xl font-bold text-white mb-2" data-id="element-598">
            {leftItem?.name}
          </h3>
          <div className="text-emerald-400 font-mono text-xl mb-4" data-id="element-599">
            {leftItem?.price === 0 ? 'Included' : `+$${leftItem?.price.toLocaleString()}`}
          </div>
          <p className="text-gray-400 text-sm max-w-md mb-4" data-id="element-600">
            Selected option for {leftItem?.category}.
          </p>
          <button onClick={() => handleSelectOption(leftItem)} className="flex items-center gap-2 px-6 py-2 rounded-lg bg-white text-gray-900 font-bold hover:bg-gray-100 transition-colors" data-id="element-601">
            <Check className="w-4 h-4" data-id="element-602" />
            Select This Option
          </button>
        </div>

        {/* Right Item Details */}
        <div className="flex flex-col items-start pl-8" data-id="element-603">
          <h3 className="text-2xl font-bold text-white mb-2" data-id="element-604">
            {rightItem?.name}
          </h3>
          <div className="text-emerald-400 font-mono text-xl mb-4" data-id="element-605">
            {rightItem?.price === 0 ? 'Included' : `+$${rightItem?.price.toLocaleString()}`}
          </div>
          <p className="text-gray-400 text-sm max-w-md mb-4" data-id="element-606">
            Selected option for {rightItem?.category}.
          </p>
          <button onClick={() => handleSelectOption(rightItem)} className="flex items-center gap-2 px-6 py-2 rounded-lg bg-white text-gray-900 font-bold hover:bg-gray-100 transition-colors" data-id="element-607">
            <Check className="w-4 h-4" data-id="element-608" />
            Select This Option
          </button>
        </div>
      </div>
    </motion.div>;
}